import React, { useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Header, Grid, Card, Icon, Button, Table } from 'semantic-ui-react';
import MultiplyHeader from '../components/MultiplyHeader/MultiplyHeader';
import { componentContainerStyle, componentContentStyle } from '../constants';
import {
    hasRole, isManagerRole, getScoreColor, getScoreDisplay,
    mapUserToTeamMember, MANAGER_ROLES, EXECUTIVE_ROLES, getLightBackgroundColor
} from './dashboardHelpers';
import GoalSettings from '../components/Dashboard/GoalSettings';
import NewAssessmentDetails from '../components/PerformanceReviews/NewAssessmentDetails';
import TeamActivityCharts from '../components/TeamDashboard/TeamActivityCharts';

const TeamDashboardPage = observer(({ usersStore, companyStore, assessmentsStore }) => {
    const [teamSortBy, setTeamSortBy] = useState('name');
    const [teamFilterDept, setTeamFilterDept] = useState('all');
    const [teamFilterRole, setTeamFilterRole] = useState('all');
    const [expandedTeamMember, setExpandedTeamMember] = useState(null);
    const [viewMode, setViewMode] = useState('cards');
    const [goalSettingsOpen, setGoalSettingsOpen] = useState(false);

    const currentUser = usersStore?.currentUser;
    const isStoreLoading = usersStore?.isLoading || assessmentsStore?.isLoading;
    const isDataReady = usersStore && currentUser && currentUser.userId;

    const isManager = hasRole(currentUser, MANAGER_ROLES);
    const isExecutive = hasRole(currentUser, EXECUTIVE_ROLES);
    const canViewTeam = isManager || isExecutive;
    const isSeniorManager = currentUser?.userType === 'Senior Manager';

    // Memoized team members
    const allTeamMembers = useMemo(() => {
        if (!canViewTeam || !usersStore?.companyUsers || !currentUser) return [];
        try {
            if (isExecutive) {
                const execTypes = ['Executive', 'Company Admin', 'admin'];
                return usersStore.companyUsers
                    .filter(user => user?.userId && user.userId !== currentUser.userId && !execTypes.includes(user.userType))
                    .map(user => mapUserToTeamMember(user, assessmentsStore, usersStore, companyStore?.company?.id));
            } else if (isSeniorManager && usersStore.getSeniorManagerVisibleUsers) {
                return usersStore.getSeniorManagerVisibleUsers(currentUser.userId)
                    .map(user => mapUserToTeamMember(user, assessmentsStore, usersStore, companyStore?.company?.id));
            } else if (isManager && usersStore.getDirectReports) {
                return usersStore.getDirectReports(currentUser.userId)
                    .map(user => mapUserToTeamMember(user, assessmentsStore, usersStore, companyStore?.company?.id));
            }
        } catch (error) {
            console.error('Error in getTeamMembers:', error);
        }
        return [];
    }, [canViewTeam, isExecutive, isManager, isSeniorManager, usersStore?.companyUsers, currentUser?.userId, assessmentsStore]);

    // Memoized filter options
    const roles = useMemo(() => [...new Set(allTeamMembers.map(m => m.role))].sort(), [allTeamMembers]);
    const managers = useMemo(() => {
        if (!isExecutive || !usersStore?.companyUsers) return [];
        return usersStore.companyUsers
            .filter(u => isManagerRole(u.userType) || u.userType === 'Company Admin' || u.userType === 'Executive' || u.userType === 'admin')
            .map(m => ({ id: m.userId, name: `${m.firstName} ${m.lastName}` }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [isExecutive, usersStore?.companyUsers]);

    const managerNameById = useMemo(() => {
        const map = {};
        (usersStore?.companyUsers || []).forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`; });
        return map;
    }, [usersStore?.companyUsers]);

    const filteredAndSortedMembers = useMemo(() => {
        return allTeamMembers
            .filter(member => {
                const roleMatch = teamFilterRole === 'all' || member.role === teamFilterRole;
                const managerMatch = teamFilterDept === 'all' || member.reportsToId === teamFilterDept || member.id === teamFilterDept;
                return roleMatch && managerMatch;
            })
            .sort((a, b) => {
                switch (teamSortBy) {
                    case 'name': return a.name.localeCompare(b.name);
                    case 'role': return a.role.localeCompare(b.role);
                    case 'manager':
                        return (managerNameById[a.reportsToId] || 'No Manager').localeCompare(managerNameById[b.reportsToId] || 'No Manager');
                    case 'avgScore': return (b.avgScore || 0) - (a.avgScore || 0);
                    case 'totalPractices': return b.totalPractices - a.totalPractices;
                    case 'recentActivity': return (a.daysSinceLastPractice ?? Infinity) - (b.daysSinceLastPractice ?? Infinity);
                    default: return 0;
                }
            });
    }, [allTeamMembers, teamFilterRole, teamFilterDept, teamSortBy, managerNameById]);

    const membersByManager = useMemo(() => {
        if (!isExecutive) return { 'My Team': filteredAndSortedMembers };
        const grouped = managers.reduce((acc, manager) => {
            const reports = filteredAndSortedMembers.filter(m => m.reportsToId === manager.id);
            if (reports.length > 0) {
                const managerCard = filteredAndSortedMembers.find(m => m.id === manager.id);
                acc[manager.name] = managerCard ? [managerCard, ...reports] : reports;
            }
            return acc;
        }, {});
        const assigned = new Set(Object.values(grouped).flat().map(m => m.id));
        const unassigned = filteredAndSortedMembers.filter(m => !assigned.has(m.id));
        if (unassigned.length > 0) {
            grouped['Unassigned'] = unassigned;
        }
        return grouped;
    }, [isExecutive, managers, filteredAndSortedMembers]);

    if (isStoreLoading || !isDataReady) {
        return (
            <div style={componentContainerStyle}>
                <MultiplyHeader />
                <div style={componentContentStyle}>
                    <div style={{ marginTop: '2em', textAlign: 'center' }}>
                        <Header as="h2" textAlign="center">
                            {isStoreLoading ? 'Loading application data...' : 'Loading user data...'}
                        </Header>
                        <div style={{ marginTop: '1em', color: '#666' }}>
                            Please wait while we load your data...
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!canViewTeam) {
        return (
            <div style={componentContainerStyle}>
                <MultiplyHeader text="Team Dashboard" />
                <div style={componentContentStyle}>
                    <div style={{ marginTop: '2em', textAlign: 'center' }}>
                        <Icon name="lock" size="huge" style={{ color: '#ccc' }} />
                        <Header as="h3" style={{ color: '#666', fontFamily: 'Montserrat' }}>
                            Access Restricted
                        </Header>
                        <p style={{ color: '#888', fontFamily: 'Montserrat' }}>
                            The Team Dashboard is available to managers, senior managers, executives, and admins.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (allTeamMembers.length === 0) {
        return (
            <div style={componentContainerStyle}>
                <MultiplyHeader text="Team Dashboard" />
                <div style={componentContentStyle}>
                    <div style={{ marginTop: '2em', textAlign: 'center' }}>
                        <Icon name="users" size="huge" style={{ color: '#ccc' }} />
                        <Header as="h3" style={{ color: '#666', fontFamily: 'Montserrat' }}>
                            No Team Members Found
                        </Header>
                        <p style={{ color: '#888', fontFamily: 'Montserrat' }}>
                            There are no team members to display at this time.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={componentContainerStyle}>
            <MultiplyHeader text="Team Dashboard" />
            <div style={componentContentStyle}>
                <Header
                    as="h2"
                    style={{
                        color: '#137fa2',
                        fontFamily: 'Montserrat',
                        fontSize: '20px',
                        fontWeight: 600,
                        marginBottom: '8px',
                        marginTop: '0px'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {isExecutive ? 'Company Overview' : 'My Team'}
                        {canViewTeam && !currentUser?.isSalesMember && (
                            <Button
                                icon="setting"
                                size="small"
                                title="Practice Goal Settings"
                                onClick={() => setGoalSettingsOpen(true)}
                                style={{ backgroundColor: 'transparent', color: '#137fa2' }}
                            />
                        )}
                    </div>
                </Header>

                {/* Filters and Sorting - Only show for executives */}
                {isExecutive && (
                    <div style={{
                        display: 'flex',
                        gap: '15px',
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                fontFamily: 'Montserrat',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#555'
                            }}>
                                Sort by:
                            </span>
                            <select
                                value={teamSortBy}
                                onChange={(e) => setTeamSortBy(e.target.value)}
                                style={{
                                    padding: '5px 10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontFamily: 'Montserrat'
                                }}
                            >
                                <option value="name">Name</option>
                                <option value="manager">Manager</option>
                                <option value="role">Role</option>
                                <option value="avgScore">Avg Score</option>
                                <option value="totalPractices">Total Practices</option>
                                <option value="recentActivity">Recent Activity</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                fontFamily: 'Montserrat',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#555'
                            }}>
                                Manager:
                            </span>
                            <select
                                value={teamFilterDept}
                                onChange={(e) => setTeamFilterDept(e.target.value)}
                                style={{
                                    padding: '5px 10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontFamily: 'Montserrat'
                                }}
                            >
                                <option value="all">All Managers</option>
                                {managers.map(manager => (
                                    <option key={manager.id} value={manager.id}>{manager.name}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                fontFamily: 'Montserrat',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#555'
                            }}>
                                Role:
                            </span>
                            <select
                                value={teamFilterRole}
                                onChange={(e) => setTeamFilterRole(e.target.value)}
                                style={{
                                    padding: '5px 10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontFamily: 'Montserrat'
                                }}
                            >
                                <option value="all">All Roles</option>
                                {roles.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#666', fontFamily: 'Montserrat' }}>
                            Showing {filteredAndSortedMembers.length} of {allTeamMembers.length} members
                        </div>
                    </div>
                )}

                {/* Team Activity Charts */}
                <TeamActivityCharts teamMembers={filteredAndSortedMembers} assessmentsStore={assessmentsStore} />

                {/* View Toggle */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
                    <Button.Group size="small">
                        <Button
                            icon="grid layout"
                            active={viewMode === 'cards'}
                            onClick={() => setViewMode('cards')}
                            style={{
                                backgroundColor: viewMode === 'cards' ? '#137fa2' : '#f0f0f0',
                                color: viewMode === 'cards' ? 'white' : '#666'
                            }}
                        />
                        <Button
                            icon="table"
                            active={viewMode === 'table'}
                            onClick={() => setViewMode('table')}
                            style={{
                                backgroundColor: viewMode === 'table' ? '#137fa2' : '#f0f0f0',
                                color: viewMode === 'table' ? 'white' : '#666'
                            }}
                        />
                    </Button.Group>
                </div>

                {/* Table View */}
                {viewMode === 'table' && (
                    <Table sortable celled selectable style={{ fontFamily: 'Montserrat', fontSize: '13px' }}>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell
                                    sorted={teamSortBy === 'name' ? 'ascending' : null}
                                    onClick={() => setTeamSortBy('name')}
                                >
                                    Name
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={teamSortBy === 'role' ? 'ascending' : null}
                                    onClick={() => setTeamSortBy('role')}
                                >
                                    Role
                                </Table.HeaderCell>
                                {isExecutive && (
                                    <Table.HeaderCell
                                        sorted={teamSortBy === 'manager' ? 'ascending' : null}
                                        onClick={() => setTeamSortBy('manager')}
                                    >
                                        Manager
                                    </Table.HeaderCell>
                                )}
                                <Table.HeaderCell
                                    sorted={teamSortBy === 'avgScore' ? 'descending' : null}
                                    onClick={() => setTeamSortBy('avgScore')}
                                >
                                    Avg Score
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={teamSortBy === 'totalPractices' ? 'descending' : null}
                                    onClick={() => setTeamSortBy('totalPractices')}
                                >
                                    Total
                                </Table.HeaderCell>
                                <Table.HeaderCell>Last 30d</Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={teamSortBy === 'recentActivity' ? 'ascending' : null}
                                    onClick={() => setTeamSortBy('recentActivity')}
                                >
                                    Days Since
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {filteredAndSortedMembers.map(member => (
                                <Table.Row
                                    key={member.id}
                                    onClick={() => setExpandedTeamMember(expandedTeamMember === member.id ? null : member.id)}
                                    style={{ cursor: 'pointer', backgroundColor: expandedTeamMember === member.id ? '#f0f8ff' : getLightBackgroundColor(member.cardColor) }}
                                >
                                    <Table.Cell>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{
                                                backgroundColor: '#137fa2',
                                                width: '28px', height: '28px', borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                <Icon name="user" style={{ color: 'white', fontSize: '13px', margin: 0 }} />
                                            </div>
                                            <strong>{member.name}</strong>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>{member.role}</Table.Cell>
                                    {isExecutive && (
                                        <Table.Cell>{managerNameById[member.reportsToId] || 'N/A'}</Table.Cell>
                                    )}
                                    <Table.Cell>
                                        <span style={{
                                            color: member.avgScore ? getScoreColor(member.avgScore) : '#999',
                                            fontWeight: 600
                                        }}>
                                            {member.avgScore ? getScoreDisplay(member.avgScore) : 'N/A'}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>{member.totalPractices}</Table.Cell>
                                    <Table.Cell>{member.practicesLast30Days}</Table.Cell>
                                    <Table.Cell>
                                        {member.daysSinceLastPractice === null || member.daysSinceLastPractice === undefined
                                            ? 'N/A'
                                            : `${member.daysSinceLastPractice}d`}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}

                {/* Card View - Team Members by Manager */}
                {viewMode === 'cards' && Object.entries(membersByManager).map(([managerName, members]) => (
                    members.length > 0 ? (
                        <div key={managerName}>
                            {isExecutive && Object.keys(membersByManager).length > 1 && (
                                <Header
                                    as="h4"
                                    style={{
                                        color: '#2c3e50',
                                        fontFamily: 'Montserrat',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        marginBottom: '12px',
                                        marginTop: '25px',
                                        paddingLeft: '5px',
                                        borderLeft: '3px solid #137fa2'
                                    }}
                                >
                                    {managerName}'s Team ({members.length})
                                </Header>
                            )}

                            <Grid stackable columns={3} style={{ marginBottom: '20px' }}>
                                {members.map((member) => {
                                    let statusText = 'Never Practiced';
                                    const statusColor = '#137fa2';

                                    if (member.daysSinceLastPractice === null || member.daysSinceLastPractice === undefined) {
                                        statusText = 'Never Practiced';
                                    } else if (member.daysSinceLastPractice === 0) {
                                        statusText = 'Completed Today';
                                    } else if (member.daysSinceLastPractice === 1) {
                                        statusText = 'Completed Yesterday';
                                    } else {
                                        statusText = `${member.daysSinceLastPractice}d ago`;
                                    }

                                    return (
                                        <Grid.Column key={member.id} style={{ minWidth: '300px' }}>
                                            <Card
                                                style={{
                                                    border: `2px solid ${member.cardColor}`,
                                                    backgroundColor: getLightBackgroundColor(member.cardColor),
                                                    borderRadius: '8px',
                                                    minHeight: '200px',
                                                    width: '100%'
                                                }}
                                            >
                                                <Card.Content style={{ padding: '15px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                                                        <div style={{
                                                            backgroundColor: '#137fa2',
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginRight: '12px'
                                                        }}>
                                                            <Icon name="user" style={{ color: 'white', fontSize: '18px' }} />
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{
                                                                fontFamily: 'Montserrat',
                                                                fontWeight: 600,
                                                                fontSize: '14px',
                                                                color: '#2c3e50'
                                                            }}>
                                                                {member.name}
                                                            </div>
                                                            <div style={{
                                                                fontFamily: 'Montserrat',
                                                                fontSize: '11px',
                                                                color: '#2c3e50'
                                                            }}>
                                                                {member.role}
                                                            </div>
                                                        </div>
                                                        <div style={{
                                                            backgroundColor: statusColor,
                                                            color: 'white',
                                                            padding: '4px 8px',
                                                            borderRadius: '12px',
                                                            fontSize: '9px',
                                                            fontWeight: 600
                                                        }}>
                                                            {statusText}
                                                        </div>
                                                    </div>

                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: (member.teamSize !== undefined || isManagerRole(member.role)) ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)',
                                                        gap: '10px',
                                                        marginBottom: '15px'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{
                                                                fontSize: '16px',
                                                                fontWeight: 'bold',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                {member.totalPractices}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '9px',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                Total
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{
                                                                fontSize: '16px',
                                                                fontWeight: 'bold',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                {member.practicesLast30Days}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '9px',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                Last 30d
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{
                                                                fontSize: '16px',
                                                                fontWeight: 'bold',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                {member.avgScore ? getScoreDisplay(member.avgScore) : 'N/A'}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '9px',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                Avg Score
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{
                                                                fontSize: '16px',
                                                                fontWeight: 'bold',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                {member.daysSinceLastPractice === null || member.daysSinceLastPractice === undefined
                                                                    ? 'N/A'
                                                                    : `${member.daysSinceLastPractice}d`}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '9px',
                                                                color: '#2c3e50',
                                                                fontFamily: 'Montserrat'
                                                            }}>
                                                                Days Since
                                                            </div>
                                                        </div>
                                                        {(member.teamSize !== undefined || isManagerRole(member.role)) && (
                                                            <div style={{ textAlign: 'center' }}>
                                                                <div style={{
                                                                    fontSize: '16px',
                                                                    fontWeight: 'bold',
                                                                    color: '#137fa2',
                                                                    fontFamily: 'Montserrat'
                                                                }}>
                                                                    {member.teamSize || 0}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '9px',
                                                                    color: '#666',
                                                                    fontFamily: 'Montserrat',
                                                                    lineHeight: '1.2',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {member.teamSize === 0 ? 'No Team' :
                                                                     member.teamSize === 1 ? '1 Team Member' :
                                                                     `${member.teamSize || 0} Team Members`}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div style={{
                                                        borderTop: '1px solid #e0e0e0',
                                                        paddingTop: '10px',
                                                        textAlign: 'center'
                                                    }}>
                                                        <Button
                                                            size="small"
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                color: '#137fa2',
                                                                border: 'none',
                                                                fontSize: '10px',
                                                                fontFamily: 'Montserrat',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => {
                                                                setExpandedTeamMember(
                                                                    expandedTeamMember === member.id ? null : member.id
                                                                );
                                                            }}
                                                        >
                                                            <Icon name='dropdown' />
                                                            {isManagerRole(member.role) ?
                                                                (member.teamSize > 0 ? `View Team (${member.teamSize}) & Practices` : 'View Team & Practices') :
                                                                'View Recent Practices'
                                                            }
                                                        </Button>
                                                    </div>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    );
                                })}
                            </Grid>
                        </div>
                    ) : null
                ))}

                {/* Expanded Team Member Details */}
                {expandedTeamMember && (() => {
                    const selectedMember = allTeamMembers.find(m => m.id === expandedTeamMember);
                    if (!selectedMember || !assessmentsStore) return null;

                    let memberAssessments = [];
                    try {
                        memberAssessments = assessmentsStore.closedAssessmentsForUser(selectedMember.id) || [];
                    } catch (e) {
                        console.error('Error loading assessments for', selectedMember.name, e);
                    }

                    return (
                        <div
                            ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            style={{
                                marginTop: '20px',
                                padding: '20px',
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <Header as="h4" style={{
                                    fontFamily: 'Montserrat',
                                    color: '#2c3e50',
                                    margin: 0
                                }}>
                                    {selectedMember.name} - Performance Reports
                                </Header>
                                <Button
                                    icon="close"
                                    size="small"
                                    style={{ backgroundColor: 'transparent', color: '#666' }}
                                    onClick={() => setExpandedTeamMember(null)}
                                />
                            </div>

                            {memberAssessments.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#2c3e50' }}>
                                    <Icon name="clipboard outline" size="large" />
                                    <p><strong>{selectedMember.name}</strong> hasn't completed any practice sessions yet.</p>
                                    <p>Encourage them to start practicing to see their feedback and progress here!</p>
                                </div>
                            ) : (
                                <NewAssessmentDetails assessments={[...memberAssessments].sort((a, b) =>
                                    new Date(b.completedDateTime) - new Date(a.completedDateTime)
                                )} />
                            )}
                        </div>
                    );
                })()}
            </div>
            <GoalSettings
                companyStore={companyStore}
                open={goalSettingsOpen}
                onClose={() => setGoalSettingsOpen(false)}
            />
        </div>
    );
});

export default TeamDashboardPage;
